import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Centralises Dashboard's auth + profile + scan-history loading in one hook.
 *
 * Responsibilities:
 *  - Hydrate user from `location.state.user` if AuthCallback forwarded it.
 *  - Otherwise call /api/auth/me; redirect to /login on failure.
 *  - Expose: { user, setUser, isLoading, userBalance, totalEarnings, totalChecks,
 *             scannedItems, setScannedItems, loadScanHistory, applyEarnings, logout }.
 *
 * NOTE on dependencies: this effect runs once on mount (guarded by `hasBootstrapped`).
 * Adding `location.state`/`navigate` to deps would cause re-auth loops on back-nav.
 */
export default function useDashboardData() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(location.state?.user || null);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const [userBalance, setUserBalance] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [totalChecks, setTotalChecks] = useState(0);
  const [scannedItems, setScannedItems] = useState([]);

  const loadScanHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/scans/`, { credentials: 'include' });
      if (res.ok) setScannedItems(await res.json());
      else console.error('Failed to load scan history:', res.status);
    } catch (err) {
      console.error('Error loading scan history:', err);
    }
  }, []);

  const applyUserSnapshot = useCallback((u) => {
    setUser(u);
    setUserBalance(u.withdrawable_balance || 0);
    setTotalEarnings(u.total_earnings || 0);
    setTotalChecks(u.total_checks || 0);
  }, []);

  const applyEarnings = useCallback((data) => {
    setUserBalance(data.total_balance);
    setTotalEarnings((prev) => prev + (data.earnings || 0));
    setTotalChecks((prev) => prev + (data.prices_checked || 5));
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    // Forwarded from AuthCallback — no need to re-auth, just load data.
    if (location.state?.user) {
      loadScanHistory();
      // Refresh balances once so the header shows the latest totals.
      fetch(`${API}/api/auth/me`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((u) => {
          if (u) applyUserSnapshot(u);
        })
        .catch(() => {});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (!res.ok) throw new Error('Not authenticated');
        if (cancelled) return;
        const u = await res.json();
        applyUserSnapshot(u);
        await loadScanHistory();
      } catch (err) {
        if (!cancelled) {
          console.error('[Dashboard] Auth check failed:', err);
          navigate('/login');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    user,
    setUser,
    isLoading,
    userBalance,
    totalEarnings,
    totalChecks,
    scannedItems,
    setScannedItems,
    loadScanHistory,
    applyEarnings,
    logout,
  };
}
